import type { QueryClient } from '@tanstack/react-query';

/**
 * Every read that shows a study's or an image's details beyond the catalogue
 * pages, by the first element of its query key: the DICOMweb study list (the
 * enterprise worklist), a study's series and their metadata (the viewer's
 * header and Information tab), the per-series facts the thumbnails and image
 * names are derived from, the viewer's navigator (the image list: names,
 * types, groups, order), the study references the study pages ask for
 * (`editable` among them), and the import page's list of the study's images.
 *
 * The catalogue's own keys (collection pages and cards, Home, Explore, the
 * study head) are the shell's to name: community passes CATALOGUE_KEYS
 * (community/api/freshness.ts), enterprise its collection pages.
 */
export const DETAILS_KEYS = ['studies', 'cases', 'series', 'series-metadata', 'study-series-meta', 'navigator', 'study-ref', 'study-drafts'] as const;

/** The reads kept per study, by its UID (the key's third element): only the edited study's are touched. */
const PER_STUDY_KEYS: readonly string[] = ['series', 'series-metadata', 'study-series-meta', 'navigator'];
/**
 * The viewer's metadata. A copy nobody is looking at is DROPPED rather than
 * marked stale: kept, the study page would open on it, start the viewer, and
 * start the viewer again when the refetch lands with the new details.
 */
const VIEWER_KEYS: readonly string[] = ['series-metadata', 'study-series-meta'];

/**
 * Whether a query shows details that a save changed, given the shell's
 * catalogue keys. With `studyUid`, a study's own DICOMweb reads count only for
 * that study: the worklist holds every row's series metadata, and one edit
 * must not fetch them all again. Without it (an image moved between cases,
 * so two studies changed) every study's do.
 */
export function showsDetails(key: readonly unknown[], catalogue: readonly string[], studyUid?: string): boolean {
  const head = key[0] as string;
  if (PER_STUDY_KEYS.includes(head)) return !studyUid || key[2] === studyUid;
  return (DETAILS_KEYS as readonly string[]).includes(head) || catalogue.includes(head);
}

/**
 * After a study's or an image's details are saved: every view that shows them
 * loads them again (the ones on screen now, the others when next opened), so
 * no header, card or list keeps the old values until a reload.
 */
export async function refreshDetailsViews(qc: QueryClient, catalogue: readonly string[], studyUid?: string): Promise<void> {
  qc.removeQueries({ type: 'inactive', predicate: (q) => VIEWER_KEYS.includes(q.queryKey[0] as string) && showsDetails(q.queryKey, catalogue, studyUid) });
  await qc.invalidateQueries({ predicate: (q) => showsDetails(q.queryKey, catalogue, studyUid) });
}
